import React from 'react';
import { motion } from 'framer-motion';
import './About.css';

const About: React.FC = () => {
  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="container">
          <motion.div
            className="about-hero-content"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="page-badge">About the Author</span>
            <h1 className="about-title">
              Kutloano<br />
              <span className="gradient-text">Mkhulisi</span>
            </h1>
            <p className="about-subtitle">Writer &bull; Enterprenuer
           &bull; Visionary</p>
          </motion.div>
        </div>
      </section>

      <section className="about-content section">
        <div className="container">
          <div className="about-grid">
            <motion.div
              className="about-image-section"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="author-image-frame">
                <div className="author-placeholder">
                  <span className="author-initials">KM</span>
                  <span className="infinity-watermark">&#8734;</span>
                </div>
              </div>
              <div className="author-stats">
                <div className="stat">
                  <span className="stat-number">1</span>
                  <span className="stat-label">Published Book</span>
                </div>
                <div className="stat">
                  <span className="stat-number">&#8734;</span>
                  <span className="stat-label">Stories to Tell</span>
                </div>
              </div>
            </motion.div>

            <motion.div
              className="about-text-section"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">The Story Behind the Stories</h2>

              <div className="about-bio">
                <p>
                  Kutloano Mkhulisi, also known as <strong>Ano</strong> or <strong>Mina</strong>,
                  is a multifaceted creative spirit hailing from The Vaal, South Africa. Growing up
                  in a vibrant community, Kutloano developed an early passion for the arts that
                  would shape the course of her life.
                </p>

                <p>
                  Relocating to Johannesburg at a pivotal point in her life, Kutloano pursued her
                  academic journey at the University of the Witwatersrand (Wits), where she immersed
                  herself in the rich cultural landscape of the city. This transition from The Vaal
                  to the bustling energy of Johannesburg became a defining chapter in her story.
                </p>

                <p>
                  A true renaissance woman, Kutloano's creative expression spans multiple art forms.
                  As a <strong>dancer</strong>, she communicates through movement what words sometimes
                  cannot capture. Her aspirations as an <strong>actress</strong> reflect her deep
                  understanding of human emotion and storytelling. But it is through
                  <strong> writing</strong> that she has found her most powerful voice.
                </p>

                <p>
                  Beyond her artistic pursuits, Kutloano founded <strong>Strength in Sisterhood (SIS)</strong>,
                  an initiative dedicated to empowering women and building supportive communities.
                  Through SIS, she channels her belief that when women lift each other up, extraordinary
                  things happen.
                </p>

                <p>
                  Her debut work, <em>Through the Fence</em>, is a testament to her storytelling
                  prowess — a narrative that explores themes of identity, freedom, and the courage
                  to question the boundaries that society places around us. Through her characters,
                  Kutloano invites readers to examine their own fences and find the strength to
                  look beyond them.
                </p>
              </div>

              <div className="about-values">
                <div className="value-item">
                  <div className="value-icon">&#9733;</div>
                  <div>
                    <h4>Strength in Sisterhood</h4>
                    <p>Founder of SIS — empowering women through community and connection</p>
                  </div>
                </div>
                <div className="value-item">
                  <div className="value-icon">&#9998;</div>
                  <div>
                    <h4>Storytelling</h4>
                    <p>Crafting narratives that challenge perspectives and inspire change</p>
                  </div>
                </div>
                <div className="value-item">
                  <div className="value-icon">&#127917;</div>
                  <div>
                    <h4>Performance Arts</h4>
                    <p>Expressing the human experience through dance and dramatic arts</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="timeline-section section">
        <div className="container">
          <h2 className="section-title" style={{ textAlign: 'center' }}>Journey</h2>
          <p className="section-subtitle" style={{ textAlign: 'center' }}>
            Key milestones in the creative journey
          </p>

          <div className="timeline">
            <TimelineItem
              year="The Vaal"
              title="Where It All Began"
              description="Born and raised in The Vaal, South Africa, surrounded by a community that sparked an early love for storytelling and the arts."
            />
            <TimelineItem
              year="Johannesburg"
              title="A New Chapter"
              description="Relocated to Johannesburg, embracing the city's vibrant energy and diverse creative landscape."
            />
            <TimelineItem
              year="Wits University"
              title="Academic Pursuit"
              description="Attended the University of the Witwatersrand, deepening both academic knowledge and artistic vision."
            />
            <TimelineItem
              year="SIS"
              title="Strength in Sisterhood"
              description="Founded the SIS initiative, creating a platform for women's empowerment and community building."
            />
            <TimelineItem
              year="2024"
              title="Through the Fence"
              description="Published debut novel, bringing Dahlia's story of courage and freedom to readers everywhere."
            />
          </div>
        </div>
      </section>
    </div>
  );
};

const TimelineItem: React.FC<{ year: string; title: string; description: string }> = ({
  year,
  title,
  description,
}) => (
  <motion.div
    className="timeline-item"
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
  >
    <div className="timeline-marker">
      <div className="timeline-dot" />
    </div>
    <div className="timeline-content">
      <span className="timeline-year">{year}</span>
      <h3>{title}</h3>
      <p>{description}</p>
    </div>
  </motion.div>
);

export default About;
